import React, { useState } from "react";
import Layout from "@/locales/en/layout/Layout";
import BigFotoBack from "@/components/bigFotoBack/BigFotoBack";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import bg from "../../../assets/markazlar/ilmiyFaoliyat.jpg";

/* ── animations ───────────────────────────────────────────── */
const fadeSlide = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── page wrapper ─────────────────────────────────────────── */
const PageWrap = styled.div`
  background: #f5f7fa;
  min-height: 60vh;
  padding: 40px 0 80px;
`;

/* ── section title ────────────────────────────────────────── */
const SectionLabel = styled.p`
  font-size: 25px;
  font-weight: 600;
  color: #15523d;
  border-bottom: 2px solid #15523d;
  padding-bottom: 10px;
  margin-bottom: 28px;
  font-family: "Montserrat", sans-serif;
  letter-spacing: 0.3px;
`;

/* ── accordion year block ─────────────────────────────────── */
const YearBlock = styled.div`
  margin-bottom: 16px;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  background: #fff;
`;

const YearHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: ${({ open }) =>
    open
      ? "linear-gradient(135deg, #15523d 0%, #1e7a58 100%)"
      : "#fff"};
  color: ${({ open }) => (open ? "#fff" : "#15523d")};
  border: none;
  cursor: pointer;
  font-family: "Montserrat", sans-serif;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.4px;
  transition: background 0.3s ease, color 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #0f3d2c 0%, #15523d 100%);
    color: #fff;
  }

  .year-info {
    display: flex;
    align-items: center;
    gap: 14px;

    .badge {
      background: ${({ open }) =>
        open ? "rgba(255,255,255,0.2)" : "rgba(21,82,61,0.1)"};
      color: ${({ open }) => (open ? "#fff" : "#15523d")};
      border-radius: 20px;
      padding: 3px 12px;
      font-size: 12px;
      font-weight: 600;
    }
  }

  .chevron {
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    transform: ${({ open }) => (open ? "rotate(180deg)" : "rotate(0deg)")};
    display: flex;
    align-items: center;
  }
`;

const YearBody = styled.div`
  overflow: hidden;
  max-height: ${({ open }) => (open ? "1200px" : "0")};
  transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.3s ease;
  opacity: ${({ open }) => (open ? 1 : 0)};
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 20px;
  padding: 24px 24px 28px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
    padding: 16px;
  }
`;

/* ── flip card ────────────────────────────────────────────── */
const FlipScene = styled.div`
  display: block;
  height: 200px;
  perspective: 900px;
  text-decoration: none;
  animation: ${fadeSlide} 0.4s ease both;
  cursor: pointer;

  &:hover .flipper {
    transform: rotateY(180deg);
  }

  .flipper {
    position: relative;
    width: 100%;
    height: 100%;
    transition: transform 0.55s cubic-bezier(0.4, 0.2, 0.2, 1);
    transform-style: preserve-3d;
  }

  /* shared face styles */
  .face {
    position: absolute;
    inset: 0;
    border-radius: 14px;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    overflow: hidden;
  }

  /* ── FRONT ── */
  .front {
    background: #fff;
    border: 1.5px solid #e5eae8;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
    transition: box-shadow 0.3s;

    .front-icon {
      width: 56px;
      height: 70px;
      background: linear-gradient(145deg, #15523d, #1e7a58);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 6px 16px rgba(21,82,61,0.3);
    }

    .front-label {
      font-family: "Montserrat", sans-serif;
      font-size: 14px;
      font-weight: 700;
      color: #1a2e25;
    }
  }

  &:hover .front {
    box-shadow: 0 12px 32px rgba(21,82,61,0.18);
  }

  /* ── BACK (cover) ── */
  .back {
    background: linear-gradient(160deg, #0d3d2b 0%, #15523d 45%, #1e7a58 100%);
    transform: rotateY(180deg);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    padding: 16px 14px 14px;
    box-shadow: 0 12px 32px rgba(21,82,61,0.3);

    /* decorative top stripe */
    &::before {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 4px;
      background: linear-gradient(90deg, #f0c040, #f5e06a, #f0c040);
      border-radius: 14px 14px 0 0;
    }

    .back-top {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }

    .back-brand {
      font-family: "Montserrat", sans-serif;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      color: rgba(255,255,255,0.6);
    }

    .back-type {
      font-family: "Montserrat", sans-serif;
      font-size: 8px;
      font-weight: 600;
      color: #f0c040;
      background: rgba(240,192,64,0.15);
      border-radius: 4px;
      padding: 2px 6px;
      letter-spacing: 0.8px;
      text-transform: uppercase;
    }

    .back-num {
      font-family: "Montserrat", sans-serif;
      font-size: 42px;
      font-weight: 900;
      color: #fff;
      line-height: 1;
      text-align: center;
      text-shadow: 0 4px 20px rgba(0,0,0,0.3);
    }

    .back-divider {
      width: 40px;
      height: 2px;
      background: rgba(255,255,255,0.25);
      border-radius: 2px;
      margin: -4px 0;
    }

    .back-year {
      font-family: "Montserrat", sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: rgba(255,255,255,0.75);
      letter-spacing: 2px;
    }

    .back-btn {
      background: rgba(255,255,255,0.15);
      border: 1px solid rgba(255,255,255,0.3);
      border-radius: 20px;
      padding: 6px 18px;
      font-size: 11px;
      font-weight: 700;
      color: #fff;
      font-family: "Montserrat", sans-serif;
      letter-spacing: 0.5px;
      backdrop-filter: blur(6px);
      text-decoration: none;
      display: inline-block;
    }

    .back-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: center;
    }

    .back-pdf {
      background: rgba(240,192,64,0.25);
      border: 1px solid rgba(240,192,64,0.6);
      border-radius: 20px;
      padding: 6px 14px;
      font-size: 11px;
      font-weight: 700;
      color: #f0c040;
      font-family: "Montserrat", sans-serif;
      letter-spacing: 0.5px;
      backdrop-filter: blur(6px);
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      gap: 4px;

      svg { flex-shrink: 0; }

      &:hover {
        background: rgba(240,192,64,0.45);
      }
    }
  }
`;

/* ── chevron SVG ──────────────────────────────────────────── */
function ChevronIcon({ color = "currentColor" }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/* ── book SVG icon (front face) ────────────────────────────── */
function BookIcon() {
  return (
    <svg width="28" height="34" viewBox="0 0 28 34" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="1" width="22" height="32" rx="3" fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5"/>
      <line x1="7" y1="9"  x2="21" y2="9"  stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="7" y1="14" x2="21" y2="14" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="7" y1="19" x2="15" y2="19" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="7" y="24" width="14" height="4" rx="1" fill="rgba(255,255,255,0.15)"/>
    </svg>
  );
}

/* ── data ─────────────────────────────────────────────────── */
const magazineData = [
  {
    year: 2026,
    issues: [
      { label: "1/2026", url: "https://online.anyflip.com/koxwe/ltmf/mobile/index.html" },
      { label: "2/2026", url: "https://online.anyflip.com/koxwe/ises/mobile/index.html" },
      { label: "3/2026", url: "https://online.anyflip.com/koxwe/tsxf/mobile/index.html" },
      { label: "4/2026", url: "https://online.anyflip.com/koxwe/sppk/mobile/index.html" },
      { label: "5/2026", url: "https://online.anyflip.com/koxwe/cgdy/mobile/index.html" },
      { label: "6/2026", url: "https://online.anyflip.com/koxwe/ysfj/mobile/index.html" },
    ],
  },
  {
    year: 2025,
    issues: [
      { label: "1/2025", url: "https://online.anyflip.com/koxwe/zfmd/index.html" },
      { label: "2/2025", url: "https://online.anyflip.com/koxwe/sjfl/index.html" },
      {
        label: "3/2025", url: "https://online.anyflip.com/koxwe/kmll/index.html",
        pdf:"https://test.tdtu.uz/storage/pages/June2026/QGf9kOI1bqtPj1ubVHzp.pdf"
      },
      { label: "4/2025", url: "https://online.anyflip.com/koxwe/uzqj/index.html" },
      { label: "5/2025", url: "https://online.anyflip.com/koxwe/dhmo/mobile/index.html" },
      { label: "6/2025", url: "https://online.anyflip.com/koxwe/cfrj/mobile/index.html" },
    ],
  },
  {
    year: 2024,
    issues: [
      { label: "1/2024", url: "https://online.anyflip.com/koxwe/ahjm/index.html" },
      { label: "2/2024", url: "https://online.anyflip.com/koxwe/uekl/mobile/index.html" },
      { label: "3/2024", url: "https://online.anyflip.com/koxwe/eumh/mobile/index.html" },
      { label: "4/2024", url: "https://online.anyflip.com/koxwe/iprr/index.html" },
      { label: "5/2024", url: "https://online.anyflip.com/koxwe/cteh/mobile/index.html" },
      { label: "6/2024", url: "https://online.anyflip.com/koxwe/bmmp/mobile/index.html" },
    ],
  },
  {
    year: 2023,
    issues: [
      { label: "1/2023", url: "https://online.anyflip.com/koxwe/ffyv/mobile/index.html" },
      { label: "2/2023", url: "https://online.anyflip.com/koxwe/ukey/mobile/index.html" },
      { label: "3/2023", url: "https://online.anyflip.com/koxwe/vakr/mobile/index.html" },
      { label: "4/2023", url: "https://online.anyflip.com/koxwe/nrzk/mobile/index.html" },
      {
        label: "5/2023",
        url: "https://online.anyflip.com/koxwe/aclx/mobile/index.html",
        pdf:"https://test.tdtu.uz/storage/pages/June2026/QGf9kOI1bqtPj1ubVHzp.pdf"
      },
      { label: "6/2023", url: "https://online.anyflip.com/koxwe/zktq/mobile/index.html" },
    ],
  },
  {
    year: 2022,
    issues: [
      { label: "1/2022", url: "https://online.anyflip.com/koxwe/ugll/mobile/index.html" },
      { label: "2/2022", url: "https://online.anyflip.com/koxwe/prza/mobile/index.html" },
      { label: "3/2022", url: "https://online.anyflip.com/koxwe/mzkb/mobile/index.html" },
      { label: "4/2022", url: "https://online.anyflip.com/koxwe/lfar/mobile/index.html" },
      { label: "5/2022", url: "https://online.anyflip.com/koxwe/ismf/mobile/index.html" },
      { label: "6/2022", url: "https://online.anyflip.com/koxwe/ncnv/mobile/index.html" },
    ],
  },
];

/* ── component ────────────────────────────────────────────── */
function Texnika() {
  const { t } = useTranslation();
  const [openYear, setOpenYear] = useState(2026);

  const toggle = (year) => setOpenYear((prev) => (prev === year ? null : year));

  return (
    <Layout>
      <BigFotoBack
        img={bg}
        text={t("header.header__bottom__nav__item__menu.menu-3.item-6")}
      />

      <PageWrap>
        <div className="container">

          <SectionLabel>{t("Magazines.item1")}</SectionLabel>

          {magazineData.map(({ year, issues }) => (
            <YearBlock key={year}>
              <YearHeader
                open={openYear === year}
                onClick={() => toggle(year)}
                aria-expanded={openYear === year}
              >
                <span className="year-info">
                  {year}-{t("Magazines.yearLabel")}
                </span>
                <span className="chevron">
                  <ChevronIcon
                    color={openYear === year ? "#fff" : "#15523d"}
                  />
                </span>
              </YearHeader>

              <YearBody open={openYear === year}>
                <CardGrid>
                  {issues.map((issue) => {
                    // parse "3/2026" → sonNum="3", issueYear="2026"
                    const [sonNum, issueYear] = issue.label.split("/");
                    return (
                      <FlipScene key={issue.label}>
                        <div className="flipper">
                          {/* FRONT */}
                          <div className="face front">
                            <div className="front-icon">
                              <BookIcon />
                            </div>
                            <span className="front-label">{issue.label}</span>
                          </div>

                          {/* BACK — muqova */}
                          <div className="face back">
                            <div className="back-top">
                              <span className="back-brand">TDTU</span>
                              <span className="back-type">{t("Magazines.scientificBadge")}</span>
                            </div>
                            <span className="back-num">{sonNum}</span>
                            <div className="back-divider" />
                            <span className="back-year">{issueYear}</span>
                            <div className="back-actions">
                              <a
                                className="back-btn"
                                href={issue.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {t("Magazines.readBtn")} →
                              </a>
                              {issue.pdf && (
                                <a
                                  className="back-pdf"
                                  href={issue.pdf}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2.5"
                                    strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                    <polyline points="7 10 12 15 17 10"/>
                                    <line x1="12" y1="15" x2="12" y2="3"/>
                                  </svg>
                                  PDF
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </FlipScene>
                    );
                  })}
                </CardGrid>
              </YearBody>
            </YearBlock>
          ))}
        </div>
      </PageWrap>
    </Layout>
  );
}

export default Texnika;
